import { use } from "react";
import {
  analyticsScopeContexts,
  getAnalyticsScopeContext,
} from "./scopeContexts.js";
import type {
  AnalyticsContexts,
  AnalyticsScopeName,
  AnalyticsScopes,
} from "./scopes.js";

/** Resolves a required scope from an accumulated context, including in builders. */
export function requireAnalyticsScope<K extends AnalyticsScopeName>(
  contexts: AnalyticsContexts,
  scope: K,
): AnalyticsScopes[K] {
  const value = contexts[scope];
  if (value === undefined) {
    throw new Error(
      `Analytics scope "${scope}" requires a matching parent <AnalyticsProvider scope="${scope}">`,
    );
  }
  return value;
}

/** Returns every accumulated scope. */
export function useAnalyticsContexts(): AnalyticsContexts;
/** Returns a required scope and throws when its provider is missing. */
export function useAnalyticsContexts<K extends AnalyticsScopeName>(
  scope: K,
): AnalyticsScopes[K];
export function useAnalyticsContexts<K extends AnalyticsScopeName>(
  scope?: K,
): AnalyticsContexts | AnalyticsScopes[K] {
  // React 19's `use` may read Context conditionally. That lets the named form
  // subscribe to exactly one flat scope instead of waking for every analytics
  // change in other location scopes.
  if (scope !== undefined) {
    const value = use(getAnalyticsScopeContext(scope));
    return requireAnalyticsScope({ [scope]: value }, scope);
  }

  const teammatePage = use(analyticsScopeContexts.teammatePage);
  const coaching = use(analyticsScopeContexts.coaching);
  return {
    ...(teammatePage === undefined ? {} : { teammatePage }),
    ...(coaching === undefined ? {} : { coaching }),
  };
}
